import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faRocket } from '@/fontawesome';
import type { PreviewLongTermRecommendation } from '@/models';

interface PreviewLongTermTradeCardProps {
  preview: PreviewLongTermRecommendation;
}

export default function PreviewLongTermTradeCard({ preview }: PreviewLongTermTradeCardProps) {
  return (
    <div className={`relative bg-white dark:bg-gray-800 rounded-lg overflow-hidden ${
      preview.boost
        ? 'border-2 border-amber-400 dark:border-amber-500 shadow-lg shadow-amber-200/50 dark:shadow-amber-900/30'
        : 'border border-gray-200 dark:border-gray-700'
    }`}>
      {/* Blurred placeholder content */}
      <div className="blur-md select-none pointer-events-none">
        {/* Main Info Section */}
        <div className="p-3 lg:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 lg:gap-6">
            {/* Chart placeholder - hidden on mobile */}
            <div className="hidden lg:block lg:col-span-3">
              <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>

            {/* Main Details */}
            <div className="lg:col-span-9 space-y-3 lg:space-y-4">
              {/* First Row */}
              <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 lg:gap-4">
                {/* Symbol placeholder */}
                <div className="lg:col-span-1">
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Symbol</div>
                  <div className="text-xl font-bold text-gray-400 dark:text-gray-500">████</div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      preview.type === 'LONG'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
                    }`}>
                      {preview.type}
                    </span>
                    {preview.boost && (
                      <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400">
                        <FontAwesomeIcon icon={faRocket} className="w-3 h-3 mr-1" />
                        BOOST
                      </span>
                    )}
                  </div>
                </div>

                {/* Letzter Einstieg placeholder */}
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Letzter Einstieg</div>
                  <div className="text-base text-gray-400 dark:text-gray-500">██.██.████</div>
                </div>

                {/* Branche placeholder - hidden on mobile */}
                <div className="hidden lg:block">
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Branche</div>
                  <div className="text-base text-gray-400 dark:text-gray-500">████████████</div>
                </div>

                {/* Ø-Einstieg placeholder */}
                <div className="hidden lg:block">
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Ø-Einstieg</div>
                  <div className="text-lg font-semibold text-gray-400 dark:text-gray-500">$███.██</div>
                </div>

                {/* Aktueller Kurs placeholder */}
                <div className="hidden lg:block">
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Aktueller Kurs</div>
                  <div className="text-lg font-semibold text-gray-400 dark:text-gray-500">$███.██</div>
                </div>

                {/* Rendite placeholder */}
                <div className="hidden lg:block">
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Rendite</div>
                  <div className="text-lg font-semibold text-gray-400 dark:text-gray-500">+██.██%</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Einstiege Section placeholder */}
        <div className="bg-gray-50 dark:bg-gray-900 px-3 py-3 lg:px-6 lg:py-4 border-t border-gray-200 dark:border-gray-700 hidden lg:block">
          <h4 className="text-sm font-medium text-gray-400 dark:text-gray-500 mb-3">
            Einstiege (█)
          </h4>
          <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>

      {/* Lock overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-white/90 via-white/50 to-transparent dark:from-gray-800/90 dark:via-gray-800/50 dark:to-transparent flex items-end justify-center pb-4 rounded-lg">
        <div className="text-center bg-white/80 dark:bg-gray-800/80 px-4 py-2 rounded-lg shadow-sm">
          <FontAwesomeIcon icon={faLock} className="w-5 h-5 text-amber-500 dark:text-amber-400 mb-1" />
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Langzeit-Trade Premium
          </p>
        </div>
      </div>
    </div>
  );
}
